import { Dialog } from '../../../widgets/dialog';
import React, { useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useRefetchableFragment } from 'react-relay/hooks';
import { ZakekeMessage, isAddToCartMessage } from 'src/shared/zakekeMessage';
import styled from 'styled-components';
import config from 'src/shared/config';
import { stringFormat, T } from 'src/shared/helpers';
import { productLogicName } from 'src/shared/productNames';
import { templateEditorDialogQuery } from './__generated__/templateEditorDialogQuery.graphql';
import { templateEditorDialog_fragment$key } from './__generated__/templateEditorDialog_fragment.graphql';

const Container = styled.div`
    position: relative;
    height: 100%;
    width: 100%;
    background: white;
    overflow: hidden;
`;

const StyledIframe = styled.iframe`
    width: 100%;
    height: 100%;
    border: 0;
`;

const FrameStatus = styled.div`
    position: absolute;
    z-index: 1;
    top: 24px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 12px;
    max-width: calc(100% - 48px);
    padding: 12px 16px;
    border: 1px solid #dedede;
    border-radius: 8px;
    background: white;
    box-shadow: 0 4px 16px #0002;
    font-size: 14px;
`;

const FrameAction = styled.button`
    color: #e66b00;
    border: 0;
    background: transparent;
    cursor: pointer;
    font-weight: 600;
    white-space: nowrap;
`;

interface Props {
    productId: string;
    designId: number | null;
    categoryId: number | null;
    areaScope?: { sideId: number; meshIds: string[]; cameraId?: string; restrictions: { isTextEnabled: boolean; isImageEnabled: boolean } };
    composerDraft?: unknown;
    onDraft?: (draft: { design: unknown; sideId: number; updateSettings: boolean }) => void;
    onSnapshot?: (draft: { design: unknown; sideId: number; updateSettings: boolean }) => void;
    onDirtyChange?: (dirty: boolean) => void;
    onDraftError?: (message: string) => void;
    onSave: (designId: number) => void;
    onClose: () => void;
}

const templateEditorDialogFragment = graphql`
    fragment templateEditorDialog_fragment on Query
    @refetchable(queryName: "templateEditorDialogFragment")
    @argumentDefinitions(modelID: { type: "ID!" }, designId: { type: "Long" }, categoryId: { type: "Long" }) {
        model(id: $modelID) {
            id
            name
            code
            iframeDesignUrl(designId: $designId, categoryId: $categoryId) {
                largeUrl
                mobileUrl
            }
        }
    }
`;

const TemplateEditorDialog = ({
    productId,
    designId,
    categoryId,
    areaScope,
    composerDraft,
    onDraft,
    onSnapshot,
    onDirtyChange,
    onDraftError,
    onSave,
    onClose
}: Props) => {
    const query = useLazyLoadQuery<templateEditorDialogQuery>(
        graphql`
            query templateEditorDialogQuery($modelID: ID!, $designId: Long, $categoryId: Long) {
                ...templateEditorDialog_fragment @arguments(modelID: $modelID, designId: $designId, categoryId: $categoryId)
            }
        `,
        { modelID: productId, designId: designId, categoryId: categoryId },
        { fetchPolicy: 'network-only' }
    );

    const [data, refetch] = useRefetchableFragment<templateEditorDialogQuery, templateEditorDialog_fragment$key>(templateEditorDialogFragment, query);

    const model = data.model;

    if (!model) throw new Error('Product not found');

    const remoteUrl = new URL(model.iframeDesignUrl.largeUrl, window.location.href);
    const useLocalCustomizer = !!areaScope && ['localhost', '127.0.0.1'].includes(window.location.hostname) && !!config.newCustomizerBaseUrl;
    const iframeUrl = useLocalCustomizer ? new URL(config.newCustomizerBaseUrl) : new URL(remoteUrl);
    iframeUrl.pathname = iframeUrl.pathname.replace(/\/Configurator\/index(?:\.mobile)?\.html$/i, '/Customizer/index.html');
    if (areaScope) {
        // Auth, product, area, preview and restrictions travel through postMessage.
        // Keep only the worker routing keys and the trusted parent origin in the URL.
        iframeUrl.search = '';
        for (const key of ['clientId', 'productCode']) {
            const value = remoteUrl.searchParams.get(key);
            if (value) iframeUrl.searchParams.set(key, value);
        }
        iframeUrl.searchParams.set('forceInterface', 'new');
        iframeUrl.searchParams.set('isTemplateEditor', '1');
        iframeUrl.searchParams.set('composerTemplateDraftMode', '1');
        iframeUrl.searchParams.set('composerHostOrigin', window.location.origin);
    }

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [retryVersion, setRetryVersion] = useState(0);
    const [readySrc, setReadySrc] = useState<string | null>(null);
    const [failedSrc, setFailedSrc] = useState<string | null>(null);
    const iframeSrc = iframeUrl.toString();
    const isReady = readySrc === iframeSrc;
    const showFallback = failedSrc === iframeSrc;

    useEffect(() => {
        if (!areaScope) return;
        let acknowledged = false;
        const stopAt = Date.now() + 30000;
        const sendLoad = () => {
            if (acknowledged || Date.now() > stopAt) return;
            iframeRef.current?.contentWindow?.postMessage(
                {
                    type: 'load',
                    queryString: remoteUrl.search,
                    parameters: {
                        isTemplateEditor: true,
                        isPresetConfigurationEditor: false,
                        convertToTemplate: false,
                        composerTemplateDraftMode: true,
                        composerTemplateSideId: areaScope.sideId,
                        composerTemplateMeshIds: areaScope.meshIds,
                        composerTemplateCameraId: areaScope.cameraId ?? null,
                        composerTemplateRestrictions: areaScope.restrictions,
                        composerTemplateDraft: composerDraft,
                        design: String(designId),
                        cartButtonText: stringFormat('Apply to {0}', productLogicName())
                    }
                },
                iframeUrl.origin
            );
        };
        const onLoaded = (event: MessageEvent) => {
            if (event.source === iframeRef.current?.contentWindow && event.origin === iframeUrl.origin && event.data?.zakekeType === 'loaded')
                acknowledged = true;
        };
        window.addEventListener('message', onLoaded);
        const interval = window.setInterval(sendLoad, 300);
        sendLoad();
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('message', onLoaded);
        };
    }, [iframeSrc, remoteUrl.search, designId, composerDraft, areaScope, retryVersion]);

    useEffect(() => {
        const onMessage = (event: MessageEvent<ZakekeMessage | { zakekeType: string }>) => {
            if (event.source !== iframeRef.current?.contentWindow || event.origin !== iframeUrl.origin) return;
            if (!areaScope && event.data && 'zakekeType' in event.data && event.data.zakekeType === 'boot-completed') {
                setReadySrc(iframeSrc);
                return;
            }
            if (areaScope && event.data && 'zakekeType' in event.data) {
                const message = event.data as { zakekeType: string; sideId?: unknown; design?: unknown; updateSettings?: unknown; dirty?: unknown };
                if (message.zakekeType === 'composer-template-ready' && message.sideId === areaScope.sideId) {
                    setReadySrc(iframeSrc);
                    return;
                }
                if (message.zakekeType === 'composer-template-dirty' && message.sideId === areaScope.sideId) {
                    onDirtyChange?.(message.dirty === true);
                    return;
                }
                if (message.zakekeType === 'composer-template-draft' || message.zakekeType === 'composer-template-snapshot') {
                    if (message.sideId === areaScope.sideId && message.design && typeof message.updateSettings === 'boolean') {
                        const callback = message.zakekeType === 'composer-template-draft' ? onDraft : onSnapshot;
                        callback?.({ design: message.design, sideId: areaScope.sideId, updateSettings: message.updateSettings });
                    }
                    return;
                }
                if (message.zakekeType === 'composer-template-error') {
                    setFailedSrc(iframeSrc);
                    onDraftError?.(typeof (message as any).message === 'string' ? (message as any).message : 'The template draft could not be prepared.');
                    return;
                }
                if (message.zakekeType === 'composer-template-snapshot-error') {
                    onDraftError?.(
                        typeof (message as any).message === 'string' ? (message as any).message : 'The local template draft could not be preserved.'
                    );
                    return;
                }
            }
            if (!areaScope && isAddToCartMessage(event.data)) onSave(parseInt(event.data.designId));
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [iframeSrc, iframeUrl.origin, areaScope?.sideId, composerDraft, onDraft, onSnapshot, onDirtyChange, onDraftError, onSave]);

    useEffect(() => {
        if (!areaScope || isReady) return;
        // A blocked cross-origin iframe can fire `load` for an empty document and
        // never fire `error`. The Customizer's area-ready message proves readiness.
        const timeout = window.setTimeout(() => setFailedSrc(iframeSrc), 30000);
        return () => window.clearTimeout(timeout);
    }, [areaScope, iframeSrc, isReady, retryVersion]);

    return (
        <>
            {areaScope && !isReady && (
                <FrameStatus role='status'>
                    <span>{showFallback ? T._('The Full Editor could not load in this window.', 'Admin') : T._('Opening Full Editor…', 'Admin')}</span>
                    {showFallback && (
                        <>
                            <FrameAction
                                type='button'
                                onClick={() => {
                                    setFailedSrc(null);
                                    setReadySrc(null);
                                    setRetryVersion((value) => value + 1);
                                }}
                            >
                                {T._('Try again', 'Admin')}
                            </FrameAction>
                            <button type='button' onClick={onClose}>
                                {stringFormat(T._('Return to {0}', 'Admin'), productLogicName())}
                            </button>
                        </>
                    )}
                </FrameStatus>
            )}
            <StyledIframe
                key={retryVersion}
                ref={iframeRef}
                data-composer-template-frame={areaScope ? '' : undefined}
                src={iframeSrc}
                onError={() => setFailedSrc(iframeSrc)}
                allow='accelerometer; magnetometer; autoplay; encrypted-media; gyroscope; picture-in-picture; camera *; xr-spatial-tracking'
                allowFullScreen
            />
        </>
    );
};

const TemplateDialogContainer = ({
    productId,
    designId,
    categoryId,
    areaScope,
    composerDraft,
    onDraft,
    onSnapshot,
    onDirtyChange,
    onDraftError,
    onSave,
    onClose
}: Props) => {
    const [editorDirty, setEditorDirty] = useState(false);
    const closeEditor = () => {
        if (areaScope && editorDirty) {
            // Closing the iframe stages its current design; only Composer Save persists it.
            const frame = document.querySelector<HTMLIFrameElement>('[data-composer-template-frame]');
            frame?.contentWindow?.postMessage({ zakekeType: 'composer-template-prepare' }, frame ? new URL(frame.src).origin : '*');
            return;
        }
        onClose();
    };
    return (
        <Dialog size='fullscreen' onClose={closeEditor}>
            <Container>
                <TemplateEditorDialog
                    productId={productId}
                    designId={designId}
                    categoryId={categoryId}
                    areaScope={areaScope}
                    composerDraft={composerDraft}
                    onDraft={onDraft}
                    onSnapshot={onSnapshot}
                    onDirtyChange={(dirty) => {
                        setEditorDirty(dirty);
                        onDirtyChange?.(dirty);
                    }}
                    onDraftError={onDraftError}
                    onSave={onSave}
                    onClose={closeEditor}
                />
            </Container>
        </Dialog>
    );
};

export default TemplateDialogContainer;
